import Modal from 'components/Shared/Modal';
import Carousel from '../Carousel';
import styles from './styles.scss';
import Button from 'components/Shared/Button';
import cn from 'classnames';
import { hasElements, NO_ELEMENT, rm } from 'utils';
import { ReactComponent } from 'models/ReactComponent';
import Markdown from '../Markdown';
import { Picture } from 'models/Picture';
import { useEffect, useState } from 'react';

interface OwnProps {
  efficiencylabels?: ReactComponent[];
  energyLabelImage2020?: Picture[];
  close: () => void;
  isOpen: boolean;
}

interface EnergyImageProps {
  efficiencylabels?: ReactComponent[];
  energyLabelImage2020?: Picture[];
}

const RenderEnergySlideTab = ({
  goToSlide,
  active
}: {
  goToSlide: () => void;
  active: boolean;
}) => (
  <Button
    className={cn(styles.energyCarouselTab, {
      [styles.energyCarouselTabActive]: active
    })}
    appearance="clear"
    onClick={goToSlide}
  >
    •
  </Button>
);

const useEnergySize = () => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const resize = () => {
      const { innerHeight, innerWidth } = window;
      const SPACING_PERCENTAGE = 0.25;
      const SPACING = innerHeight * SPACING_PERCENTAGE;
      setWidth(innerWidth - SPACING);
      setHeight(innerHeight - SPACING);
    };

    resize();
    addEventListener('resize', resize);
    return () => removeEventListener('resize', resize);
  }, []);

  return {
    width,
    height
  };
};

const EnergyInfo = ({ content }: { content: ReactComponent }) => {
  const { height } = useEnergySize();
  return <Markdown className={styles.svgContainer} style={{ maxHeight: height }} {...content} />;
};

const ExactlyOneEnergyImage = ({ energyLabelImage2020, efficiencylabels }: EnergyImageProps) => {
  if (energyLabelImage2020 && energyLabelImage2020.length === 1) {
    return <EnergyImage2020 content={energyLabelImage2020[0]} />;
  }
  if (efficiencylabels && efficiencylabels.length === 1) {
    return <EnergyInfo content={efficiencylabels[0]} />;
  }
  return NO_ELEMENT;
};

const EnergyImage2020 = ({ content }: { content: Picture }) => (
  <img className={styles.energyLabelImage2020} src={content.src} />
);

const CarouselEnergyImages = (props: EnergyImageProps) => {
  if (props.energyLabelImage2020 && props.energyLabelImage2020.length > 1) {
    return (
      <Carousel
        autoFocus
        className={styles.energyCarousel}
        classNames={{
          controls: styles.energyCarouselControls,
          tabs: styles.energyCarouselTabs
        }}
        displayControls
        slidesData={props.energyLabelImage2020}
        RenderSlideTab={RenderEnergySlideTab}
      >
        {(slide: Picture, i) => <EnergyImage2020 key={i} content={slide} />}
      </Carousel>
    );
  }
  if (props.efficiencylabels && props.efficiencylabels.length > 1) {
    return (
      <Carousel
        autoFocus
        className={styles.energyCarousel}
        classNames={{
          controls: styles.energyCarouselControls,
          tabs: styles.energyCarouselTabs
        }}
        displayControls
        slidesData={props.efficiencylabels}
        RenderSlideTab={RenderEnergySlideTab}
      >
        {(slide: ReactComponent, i) => <EnergyInfo key={i} content={slide} />}
      </Carousel>
    );
  }
  return NO_ELEMENT;
};

export const EnergyModal = (props: OwnProps) => {
  const { efficiencylabels, energyLabelImage2020, isOpen, close } = props;
  return (
    <Modal show={isOpen} onClose={close}>
      {!hasElements(efficiencylabels) && !hasElements(energyLabelImage2020) && (
        <p>{rm('product_no_energy_label')}</p>
      )}
      <ExactlyOneEnergyImage
        efficiencylabels={efficiencylabels}
        energyLabelImage2020={energyLabelImage2020}
      />
      <CarouselEnergyImages
        efficiencylabels={efficiencylabels}
        energyLabelImage2020={energyLabelImage2020}
      />
      <Button onClick={close}>{rm('shared_button_close')}</Button>
    </Modal>
  );
};
